import axios from 'axios';

// Server-backed store of saved sandbox scenarios. A "saved scenario" is the WHOLE configuration a
// planner built in the Capacity Sandbox — demand mix, hypothetical station/cycle edits, and the flow
// policy (transfer mode/size, per-edge overrides, starting inventory). The Capacity Sandbox, the Flow
// planner and the tablet Plan tab all read this store, which is how a saved scenario "appears on
// Flow" and becomes dispatchable from its chip.
//
// This used to be localStorage. That made it PER-DEVICE: a scenario tuned on a laptop was invisible
// on the booth tablet, and clearing site data destroyed it silently. It now lives in
// `SandboxScenario` and is company-scoped server-side, so the device stops mattering.
//
// The wire shape is FLAT (`{ id, name, createdAt, ...config }`) — the same shape the two builders at
// the bottom of this file already consume. The server stores the config half opaquely; these
// builders remain the single translation from "what a planner authored" into "what the planning API
// accepts", so there is exactly one place where that mapping can be wrong.

const API_BASE = '/machinesim/api/planning';
const auth = (token) => (token ? { headers: { Authorization: `Bearer ${token}` } } : {});

export async function fetchSaved(company, token) {
  const res = await axios.get(`${API_BASE}/companies/${company}/sandbox-scenarios`, auth(token));
  return res.data?.scenarios ?? [];
}

export async function createSaved(company, scn, token) {
  const res = await axios.post(`${API_BASE}/companies/${company}/sandbox-scenarios`, scn, auth(token));
  return res.data?.scenario ?? null;
}

export async function deleteSaved(company, id, token) {
  await axios.delete(`${API_BASE}/companies/${company}/sandbox-scenarios/${id}`, auth(token));
}

// ── One-time migration off the old per-browser store ─────────────────────────
// Anyone who saved scenarios before this moved server-side still has them in localStorage on that
// machine, and they are exactly the scenarios most likely to matter (they were worth saving once).
// On first load per company we push them up, then set a flag.
//
// The legacy rows are NOT deleted. A migration that clears the only copy of something and then hits
// a 403 on a device that is signed out has destroyed the user's work; leaving the old key costs a
// few KB and makes this reversible.
const LEGACY_KEY = (company) => `mh:sandboxScenarios:v1:${company || 'default'}`;
const MIGRATED_KEY = (company) => `mh:sandboxScenarios:migrated:v1:${company || 'default'}`;

export function listLegacyLocal(company) {
  try {
    const raw = localStorage.getItem(LEGACY_KEY(company));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

/**
 * Push any pre-server scenarios for this company up, once. Returns the number migrated.
 *
 * `existing` is the server list already fetched by the caller — passed in rather than re-fetched so
 * this costs no extra round-trip on the overwhelmingly common path where there is nothing to do.
 * Matching is by NAME: the legacy ids were client-generated (`scn_<ts>_<rand>`) and carry no meaning
 * server-side, so a name collision is the only signal that a scenario is already up there. That
 * makes a re-run idempotent, which matters because the flag lives in the same localStorage that a
 * cleared browser will have lost.
 */
export async function migrateLegacyLocal(company, token, existing = []) {
  let done = false;
  try { done = localStorage.getItem(MIGRATED_KEY(company)) === '1'; } catch { /* private mode */ }
  if (done) return 0;

  const legacy = listLegacyLocal(company);
  if (!legacy.length) {
    try { localStorage.setItem(MIGRATED_KEY(company), '1'); } catch { /* ignore */ }
    return 0;
  }

  const have = new Set(existing.map((s) => s.name));
  let migrated = 0;
  for (const scn of legacy) {
    if (have.has(scn.name)) continue;
    // eslint-disable-next-line no-unused-vars
    const { id, createdAt, ...rest } = scn;
    await createSaved(company, rest, token);   // let a failure propagate: a partial migration that
    migrated += 1;                             // reports success would strand the rest silently.
  }
  try { localStorage.setItem(MIGRATED_KEY(company), '1'); } catch { /* ignore */ }
  return migrated;
}

// ── Pure builders (unchanged; the single translation into planning-API policy) ──

// Build the demandOverride array (partId → quantity) the planning API expects.
export function demandOverrideFromSaved(scn) {
  return Object.entries(scn?.demand || {}).map(([partId, quantity]) => ({ partId, quantity }));
}

// Build the server-side planning policy from a saved scenario's flow-policy + castings. Mirrors the
// Sandbox's live buildPolicy so a saved scenario dispatches identically to dispatching it live.
export function policyFromSaved(scn) {
  const p = scn?.policy || {};
  const mode = p.xferMode || 'TRANSFER_BATCH';
  const transfer = { mode };
  if (mode === 'TRANSFER_BATCH') transfer.batchPercent = Math.max(0.01, Number(p.xferPct ?? 25) / 100);
  const byStage = {};
  for (const [mt, v] of Object.entries(p.edgeBatch || {})) {
    if (v !== '' && v != null && Number(v) > 0) byStage[mt] = { mode: 'TRANSFER_BATCH', batch: Number(v) };
  }
  if (Object.keys(byStage).length) transfer.byStage = byStage;

  const policy = { transfer };
  const startingWip = {};
  for (const [partId, v] of Object.entries(scn?.castings || {})) {
    if (v !== '' && v != null && Number(v) > 0) startingWip[partId] = Number(v);
  }
  if (Object.keys(startingWip).length) policy.startingWip = startingWip;

  // Per-stage cycle-time overrides (absolute seconds) — computed at save time so this is refCycle-free.
  const cycleOverrides = {};
  for (const [g, v] of Object.entries(scn?.cycleOverrides || {})) {
    if (v != null && Number(v) > 0) cycleOverrides[g] = Number(v);
  }
  if (Object.keys(cycleOverrides).length) policy.cycleOverrides = cycleOverrides;

  // Per-stage, per-tool tool-life preset overrides (units). Shape: { machineType: { toolKey: units } }.
  // A bare number per stage is still accepted (older shape) and left as-is for the server to apply
  // stage-wide. Shorter life → more frequent tool changes.
  const presetOverrides = {};
  for (const [g, v] of Object.entries(scn?.presetOverrides || {})) {
    if (v != null && typeof v === 'object') {
      const perTool = {};
      for (const [toolKey, u] of Object.entries(v)) {
        if (u !== '' && u != null && Number(u) > 0) perTool[toolKey] = Math.round(Number(u));
      }
      if (Object.keys(perTool).length) presetOverrides[g] = perTool;
    } else if (v !== '' && v != null && Number(v) > 0) {
      presetOverrides[g] = Math.round(Number(v));
    }
  }
  if (Object.keys(presetOverrides).length) policy.presetOverrides = presetOverrides;

  // Real-time mode: run each op at its program's full compiled length instead of an accelerated cycle,
  // so the FOCAS block/line sweep smoothly. Server resolves per-op program duration; wins over
  // cycleOverrides for ops that have a program (foundry ops keep their override / seeded time).
  if (p.realTime) policy.cycleMode = 'PROGRAM';
  return policy;
}
